<template>
  <section
    class="valuation-tab"
    :dir="language === 'ar' ? 'rtl' : 'ltr'"
    aria-labelledby="valuation-heading">
    <header class="valuation-header">
      <div>
        <span>ROOM VALUATION</span>
        <h2 id="valuation-heading">
          Room Booking Information
        </h2>
      </div>
    </header>

    <div class="valuation-grid">
      <article
        v-for="card in cards"
        :key="card.key"
        class="valuation-card"
        :class="card.key"
        :dir="isArabic ? 'rtl' : 'ltr'">
        <header>
          <span class="card-icon"><b-icon :icon="card.icon" /></span>
          <h3>{{ card.title }}</h3>
        </header>
        <p>{{ card.content }}</p>
        <footer>Language: {{ languageLabel }}</footer>
      </article>
    </div>

    <p class="language-note">
      <b-icon icon="arrow-repeat" />
      Content changes automatically when the page language is changed.
    </p>
  </section>
</template>

<script>
import { BIcon } from 'bootstrap-vue'
import valuationSamples from '../data/valuationSamples'

const EMPTY_CONTENT = 'Content is not available for this room.'
const LANGUAGE_LABELS = {
  en: 'English (en)',
  ar: 'Arabic (ar)',
  es: 'Spanish (es)',
  tr: 'Turkish (tr)',
}

export default {
  name: 'ValuationTab',
  components: { BIcon },
  props: {
    language: {
      type: String,
      default: 'en',
    },
    sampleIndex: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    sampleCount() {
      return valuationSamples.length
    },
    selectedSample() {
      if (
        Number.isInteger(this.sampleIndex)
        && this.sampleIndex >= 0
        && this.sampleIndex < valuationSamples.length
      ) {
        return valuationSamples[this.sampleIndex]
      }
      return valuationSamples[0] || { id: 0, translations: {} }
    },
    selectedLanguage() {
      const requested = String(this.language || '').trim().toLowerCase()
      const translations = this.selectedSample.translations || {}
      return translations[requested] ? requested : 'en'
    },
    selectedTranslation() {
      const translations = this.selectedSample.translations || {}
      return translations[this.selectedLanguage] || translations.en || {}
    },
    isArabic() {
      return this.selectedLanguage === 'ar'
    },
    displayPosition() {
      const position = valuationSamples.indexOf(this.selectedSample) + 1
      return String(Math.max(position, 1)).padStart(2, '0')
    },
    languageLabel() {
      return LANGUAGE_LABELS[this.selectedLanguage] || LANGUAGE_LABELS.en
    },
    cards() {
      return [
        {
          key: 'booking-notes',
          title: 'Booking Notes',
          icon: 'file-earmark-text',
          content: this.safeContent(this.selectedTranslation.bookingNotes),
        },
        {
          key: 'cancellation-policy',
          title: 'Cancellation Policy',
          icon: 'shield-check',
          content: this.safeContent(this.selectedTranslation.cancellationPolicy),
        },
      ]
    },
  },
  methods: {
    safeContent(value) {
      const content = String(value == null ? '' : value).trim()
      return content || EMPTY_CONTENT
    },
  },
}
</script>

<style scoped>
.valuation-tab {
  min-height: 420px;
  padding: clamp(22px, 3vw, 36px);
  border: 1px solid #e1dce9;
  border-radius: 22px;
  color: #2a213a;
  background:
    radial-gradient(circle at 94% 4%, rgba(124, 58, 237, .07), transparent 23rem),
    #fff;
  box-shadow: 0 22px 58px rgba(37, 23, 58, .1);
  font-family: 'Montserrat', sans-serif;
}
.valuation-header {
  padding-bottom: 20px;
  border-bottom: 1px solid #ece6f2;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  direction: ltr;
}
.valuation-header > div { text-align: left; }
.valuation-header span {
  display: block;
  margin-bottom: 5px;
  color: #7c3aed;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .16em;
  text-transform: uppercase;
}
.valuation-header h2 {
  margin: 0;
  color: #241b3f;
  font-size: clamp(22px, 2.2vw, 31px);
  font-weight: 700;
  letter-spacing: -.03em;
}
.room-badge {
  padding: 9px 13px;
  border: 1px solid #d8c7ee;
  border-radius: 999px;
  color: #5b21b6;
  background: #f5efff;
  font-size: 10px;
  font-weight: 800;
  white-space: nowrap;
}
.valuation-grid {
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 18px;
}
.valuation-card {
  min-width: 0;
  min-height: 310px;
  padding: 24px;
  border: 1px solid #e4ddeb;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(145deg, #fff, #fdfbff);
  box-shadow: 0 13px 34px rgba(63, 34, 88, .07);
}
.valuation-card::before {
  content: '';
  height: 4px;
  margin: -24px -24px 20px;
  background: linear-gradient(90deg, #7c3aed 0%, #7c3aed 60%, #a21caf 100%);
}
.valuation-card.booking-notes::before {
  background: linear-gradient(90deg, #7c3aed 0%, #7c3aed 60%, #2563eb 100%);
}
.valuation-card > header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.card-icon {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(145deg, #8b5cf6, #6d28d9);
  box-shadow: 0 9px 20px rgba(109, 40, 217, .22);
  font-size: 18px;
}
.cancellation-policy .card-icon {
  background: linear-gradient(145deg, #c026d3, #7c3aed);
}
.valuation-card h3 {
  margin: 0;
  color: #2d2140;
  font-size: 18px;
  font-weight: 800;
}
.valuation-card > p {
  margin: 24px 0;
  color: #5e526b;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.9;
  overflow-wrap: anywhere;
  white-space: pre-line;
}
.valuation-card[dir='rtl'] {
  font-family: Arial, 'Noto Sans Arabic', sans-serif;
  text-align: right;
}
.valuation-card[dir='rtl'] > header { flex-direction: row; }
.valuation-card footer {
  margin-top: auto;
  padding-top: 17px;
  border-top: 1px solid #eee8f3;
  color: #7c3aed;
  font-size: 10px;
  font-weight: 700;
}
.language-note {
  margin: 20px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #85788f;
  font-size: 10px;
  text-align: center;
  direction: ltr;
}
.language-note .b-icon { color: #a21caf; }
@media (max-width: 900px) {
  .valuation-grid { grid-template-columns: 1fr; }
  .valuation-card { min-height: 260px; }
}
@media (max-width: 560px) {
  .valuation-tab { padding: 16px; border-radius: 17px; }
  .valuation-header { align-items: flex-start; flex-direction: column; }
  .valuation-card { min-height: 240px; padding: 18px; border-radius: 15px; }
  .valuation-card::before { margin: -18px -18px 17px; }
  .valuation-card > p { margin: 19px 0; font-size: 13px; }
}
</style>
